/**
 * 页签内可见停留 ≥10s → visit.engaged_10s（每 session 一次；51 只报事件名）。
 */
import { trackAnalyticsLog, withAcquisitionProps } from '@/utils/analytics-track';

const STORAGE_KEY = 'deepsage-analytics-visit-engaged-10s';
const ENGAGED_MS = 10_000;

let setupDone = false;

function hasTracked() {
  if (typeof sessionStorage === 'undefined') return true;
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return true;
  }
}

/** session 内是否已打过 visit.engaged_10s（供 engaged_action 判定） */
export function hasVisitEngaged10sTracked() {
  return hasTracked();
}

const ENGAGED_ACTION_STORAGE = 'deepsage-analytics-visit-engaged-action';

/** 视为「关键行为」的本地 key（须已走过 visit.engaged_10s） */
const ENGAGED_ACTION_TRIGGER_KEYS = new Set([
  'deposit.open',
  'register.open',
  'login.open',
  'market.open',
  'market.open_newbie',
  'trade.dialog.open',
  'trade.submit.start',
  'auth.register',
  'trial_fund.claim',
  'trial_fund.open',
]);

function hasEngagedActionTracked() {
  if (typeof sessionStorage === 'undefined') return true;
  try {
    return sessionStorage.getItem(ENGAGED_ACTION_STORAGE) === '1';
  } catch {
    return true;
  }
}

function markEngagedActionTracked() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(ENGAGED_ACTION_STORAGE, '1');
  } catch {
    /* ignore */
  }
}

/**
 * 在 trackLog 成功写入后调用：10s 有效访问后的首次关键行为。
 * 由 analytics store 动态 import，避免与 analytics-track 循环依赖。
 * @param {string} key
 * @param {Record<string, unknown>|null|undefined} [props]
 */
export function maybeTrackVisitEngagedAction(key, props) {
  const localKey = String(key || '').trim();
  if (!ENGAGED_ACTION_TRIGGER_KEYS.has(localKey)) return;
  if (localKey === 'visit.engaged_action') return;
  if (!hasTracked()) return;
  if (hasEngagedActionTracked()) return;
  markEngagedActionTracked();
  trackAnalyticsLog({
    key: 'visit.engaged_action',
    label: '有效访问后关键行为',
    value: withAcquisitionProps({
      triggerKey: localKey,
      ...(props && typeof props === 'object' ? props : {}),
    }),
  });
}

function markTracked() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * @param {import('vue-router').Router} [router]
 */
export function setupVisitEngaged10s(router) {
  if (setupDone || typeof window === 'undefined' || typeof document === 'undefined') return;
  setupDone = true;
  if (hasTracked()) return;

  let accruedMs = 0;
  let tickStartedAt = 0;
  let fired = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  let timer = null;

  function clearTimer() {
    if (timer == null) return;
    clearTimeout(timer);
    timer = null;
  }

  function fire() {
    if (fired) return;
    fired = true;
    clearTimer();
    markTracked();
    const routeName =
      (typeof router?.currentRoute?.value?.name === 'string' && router.currentRoute.value.name) ||
      undefined;
    trackAnalyticsLog({
      key: 'visit.engaged_10s',
      label: '有效访问10秒',
      value: withAcquisitionProps({
        dwellMs: Math.max(ENGAGED_MS, Math.round(accruedMs)),
        routeName,
      }),
    });
  }

  function flushVisibleSlice() {
    if (!tickStartedAt) return;
    accruedMs += Date.now() - tickStartedAt;
    tickStartedAt = 0;
  }

  function schedule() {
    clearTimer();
    if (fired || document.visibilityState !== 'visible') return;
    const remain = ENGAGED_MS - accruedMs;
    if (remain <= 0) {
      fire();
      return;
    }
    tickStartedAt = Date.now();
    timer = setTimeout(() => {
      timer = null;
      if (document.visibilityState !== 'visible') return;
      flushVisibleSlice();
      if (accruedMs >= ENGAGED_MS) fire();
      else schedule();
    }, remain);
  }

  function onVisibility() {
    if (fired) return;
    if (document.visibilityState === 'visible') {
      schedule();
      return;
    }
    flushVisibleSlice();
    clearTimer();
    if (accruedMs >= ENGAGED_MS) fire();
  }

  document.addEventListener('visibilitychange', onVisibility);
  if (document.visibilityState === 'visible') schedule();
}
